import React from 'react';
import { ArrowDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import HeroImage from '../assets/herosec_image.8097ca73.jpg';
import ClinicalPsychologyImage from '../assets/cbt-image-2.png';

export default function ClinicalPsychologyPage() {
  const navigate = useNavigate();

  const assessmentQuestions = [
    {
      id: 1,
      question: "I have stress, anxiety or other psychological conditions in my personal life, my family life, workplace, employment, business etc."
    },
    {
      id: 2,
      question: "I have multiple difficulties to address, including childhood and current issues, abuses, bullying, trauma, trust, don't believe myself, I am worthless, low self-esteem etc."
    },
    {
      id: 3,
      question: "I need coping techniques to deal with my difficulties."
    },
    {
      id: 4,
      question: "I am not good enough. Or I feel that I am a failure or an embarrassment to myself or to my family."
    },
    {
      id: 5,
      question: "I can't stop thinking about what is bothering me. Or my mind keeps running around a million miles in my head or my thinking goes round like a vicious cycle."
    },
    {
      id: 6,
      question: "I have a difficulty or a conflict in my relationship with someone that I want to explore deeper. Or I am struggling to deal with a major change in my life, bereavement or grief and loss. Or I am finding it difficult to start or keep a good relationship."
    },
    {
      id: 7,
      question: "I want a safe space to talk how I am feeling and thinking about my difficulty."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hero Section with Background Image */}
      <section className="relative min-h-[600px] flex items-end bg-cover bg-center" 
               style={{
                 backgroundImage: `url(${HeroImage})`
               }}>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-0">
          <div className="flex flex-col lg:flex-row items-end gap-8 lg:gap-12">
            {/* Left Content Card - Positioned at bottom */}
            <div className="lg:w-1/2 w-full relative mb-0">
              <div className="bg-slate-700/50 backdrop-blur-sm p-8 md:p-10 lg:p-12 rounded-t-lg shadow-lg">
                <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Clinical Psychology Services
                </h1>
                <p className="text-sm md:text-base text-white/90 leading-relaxed mb-8">
                  Our clinical psychology services offer personalized psychological therapy for 
                  anxiety, trauma, and other mental health problems, helping people achieve emotional 
                  balance and improved quality of life.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mb-16">
                  <button onClick={() => navigate('/book-now')} className="bg-white hover:bg-gray-100 text-green-600 px-6 py-3 rounded font-medium transition-colors">
                    BOOK NOW
                  </button>
                </div>
              </div>
              {/* Arrow button positioned at bottom right corner of card */}
              <button onClick={() => navigate('/book-now')} className="absolute bottom-0 right-0 bg-green-600 hover:bg-green-700 text-white p-3 rounded-tl-lg transition-colors shadow-lg">
                <ArrowDown className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-12 md:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Clinical Psychology at Health City */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-black mb-6">
              Clinical Psychology at Health City
            </h2>
            <p className="text-sm md:text-base text-black font-medium mb-6">
              What Support Our Clinical, Counselling and Health Psychologists offer you?
            </p>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed max-w-4xl mx-auto">
              Most psychologists are trained at doctor level to help you talk, process your feelings, gain a deep 
              understanding, and acquire practical ways or techniques to cope with almost any difficulty to resolve your 
              issues or achieve resilience in living your life from relationships to business and career. Most 
              psychologists may help you by doing more than one set of treatment skills, e.g. Cognitive behavioural 
              therapy (CBT), Eye movement Desensitisation reprocessing therapy (EMDR), counselling, coaching, etc.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Left Content */}
            <div className="lg:w-1/2 space-y-8">
              <div>
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-black mb-6">
                  Qualified, Accredited, Registered Clinical Psychologists
                </h3>
                
                <div className="space-y-4 text-sm md:text-base text-gray-700 leading-relaxed">
                  <p>
                    Having a qualified and registered clinical psychologist ensures you receive expert care 
                    tailored to your mental health needs. These professionals are equipped to address 
                    complex emotional and psychological issues, offering evidence-based therapies. With their 
                    expertise, you can trust that your treatment is safe, effective, and designed to foster long-
                    term mental well-being and positive life changes.
                  </p>
                </div>
              </div>

              {/* Trust Section */}
              <div>
                <h3 className="text-lg md:text-xl font-bold text-black mb-4">
                  Why Trust Us With Your Mental Health Issues?
                </h3>
                
                <div className="space-y-3 text-sm md:text-base text-gray-700">
                  <div>
                    <span className="font-medium text-black">Qualified and Experienced Professionals</span>
                  </div>
                  <div>
                    <span className="font-medium text-black">Confidential and Safe Support</span>
                  </div>
                  <div>
                    <span className="font-medium text-black">Compassionate Care</span>
                  </div>
                  <div>
                    <span className="font-medium text-black">Online & In-person Sessions</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Content - Image */}
            <div className="lg:w-1/2">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img 
                  src={ClinicalPsychologyImage} 
                  alt="Clinical psychology session" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Are You Looking For? Section */}
      <section className="py-20 bg-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">What Are You Looking For?</h2>
          
          <div className="space-y-6">
            {assessmentQuestions.map((item, index) => (
              <div key={item.id} className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
                <div className="flex gap-4">
                  <div className="text-2xl font-bold text-black opacity-20 shrink-0">
                    {String(item.id).padStart(2, '0')}
                  </div>
                  <div className="flex-1">
                    <p className="text-lg text-white mb-4">{item.question}</p>
                    <div className="flex gap-4">
                      <button className="bg-gray-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-500 transition-colors">
                        Yes
                      </button>
                      <button className="bg-gray-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-500 transition-colors">
                        No
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <button 
              onClick={() => navigate('/contact')}
              className="bg-gray-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-500 transition-colors"
            >
              NEXT
            </button>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-300 leading-relaxed max-w-4xl mx-auto">
              Disclaimer: The questions and the questionnaires on this website do not give you a diagnosis. 
              You must ask to be assessed by and to ask for and use expert medical advice from a qualified 
              medical health professional if you are worried about your health or the health of someone else. 
              Healthcity and its affiliated organizations do not provide Emergency Services, as such the phone 
              numbers, emails and other contact details must not be used by you if you are risk to yourself 
              or to other people. If you are at risk or feel distressed, do contact your local emergency 
              services or your local family doctor for urgent advice and support. You can also find helplines 
              on the Keep Save/Risk Menus of this website.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}